import * as React from 'react'

import { IconEye, IconEyeOff } from '../lib/icons'
import { cn } from '../lib/cn'
import { Input } from './input'

// Owns only the revealed/hidden state: the value stays with whoever passes `onChange`, so
// react-hook-form keeps its registration.

export interface PasswordInputProps extends Omit<React.ComponentPropsWithoutRef<'input'>, 'type'> {
  /** Announced by the toggle while the password is hidden. */
  readonly showLabel: string
  /** Announced by the toggle while the password is visible. */
  readonly hideLabel: string
}

export const PasswordInput = React.forwardRef<HTMLInputElement, PasswordInputProps>(
  ({ className, showLabel, hideLabel, disabled, ...props }, ref) => {
    const [revealed, setRevealed] = React.useState(false)
    const Icon = revealed ? IconEyeOff : IconEye

    return (
      <div className="relative">
        <Input
          ref={ref}
          type={revealed ? 'text' : 'password'}
          disabled={disabled}
          // Room for the toggle, so a long password never runs under it.
          className={cn('pr-10', className)}
          {...props}
        />
        <button
          // Not a `Button`: this one sits inside a control and takes the field's height, and
          // `type` is load-bearing — a bare button inside a form submits it.
          type="button"
          onClick={() => setRevealed((shown) => !shown)}
          disabled={disabled}
          aria-label={revealed ? hideLabel : showLabel}
          // Out of the tab order: between a password and its submit the expected next stop is
          // the button, not an optional convenience. It stays reachable by pointer.
          tabIndex={-1}
          className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:focus-volee disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Icon className="size-4" aria-hidden="true" />
        </button>
      </div>
    )
  },
)
PasswordInput.displayName = 'PasswordInput'
